'use client';

import { FloatingIcon } from '@fastfive-dev/fastfive-design-system/icons/etc';
import Link from 'next/link';

import { COMPANY } from '@/shared/constants/company';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
import { FORM_KEY, openPopupTab } from '@publishing/modules/forms';

import { ESTIMATE_HREF, openDefaultConsultPopup } from './leadActions';

/**
 * 흰 배경 아이템(상담·전화)의 hover — 시안이 흰색 75% 위에 slate 5% 를 겹친 2단 그라디언트다.
 * GNB 로그인 버튼과 동일한 처리라 표현을 맞춘다.
 */
const HOVER_ON_WHITE =
  'hover:bg-[rgba(255,255,255,0.75)] hover:[background-image:linear-gradient(rgba(133,153,183,0.05),rgba(133,153,183,0.05))]';

type Props = {
  /** 오피스 견적 버튼 노출. */
  showEstimate?: boolean;
  /** 상담 신청 버튼 노출. */
  showConsult?: boolean;
  /** 전화 버튼 노출. */
  showCall?: boolean;
  /** 상담 신청 액션. 없으면 리드폼 팝업(탭형) 오픈. */
  consultAction?: () => void;
};

/** PC 전용 우측 세로 레일 (모바일은 `<LeadBar />` 하단 바). */
export function LeadButton({
  showEstimate = false,
  showConsult = false,
  showCall = false,
  consultAction,
}: Props) {
  if (!showEstimate && !showConsult && !showCall) return null;

  return (
    <div className="rounded-16 desktop:flex hidden w-80 flex-col overflow-hidden border border-gray-500 bg-gray-100 shadow-[0px_5px_10px_0px_rgba(0,0,0,0.05),0px_2px_7px_0px_rgba(0,0,0,0.05),0px_0px_4px_0px_rgba(0,0,0,0.08)]">
      {showEstimate && (
        <Link
          href={ESTIMATE_HREF}
          aria-label="오피스 무료 견적"
          className="bg-primary-600 hover:bg-primary-700 flex h-80 w-80 flex-col items-center justify-center gap-2 text-gray-100"
          {...gtmClick(GTM_EVENT.FLOATING, '오피스 견적')}
        >
          <FloatingIcon type="estimate" value="white" />
          <span className="text-body-300 font-medium">오피스 견적</span>
        </Link>
      )}

      {showConsult && <ConsultButton action={consultAction} />}

      {showConsult && showCall && (
        <div className="flex h-full w-full items-center justify-center">
          <div className="h-1 w-50 bg-gray-400"></div>
        </div>
      )}

      {showCall && (
        <a
          href={COMPANY.CUSTOMER_CENTER_TEL_HREF}
          aria-label={`전화 상담 ${COMPANY.CUSTOMER_CENTER_NUMBER}`}
          className={`flex h-80 w-80 flex-col items-center justify-center gap-2 bg-gray-100 text-gray-900 ${HOVER_ON_WHITE}`}
          {...gtmClick(GTM_EVENT.FLOATING, '전화 상담')}
        >
          <FloatingIcon type="call" />
          <span className="text-body-200 font-medium">
            {COMPANY.CUSTOMER_CENTER_NUMBER}
          </span>
        </a>
      )}
    </div>
  );
}

function ConsultButton({ action }: { action?: () => void }) {
  return (
    <button
      type="button"
      onClick={action ?? openDefaultConsultPopup}
      aria-label="상담 신청"
      className={`flex h-80 w-80 flex-col items-center justify-center gap-2 bg-gray-100 text-gray-900 ${HOVER_ON_WHITE}`}
      {...gtmClick(GTM_EVENT.FLOATING, '상담 신청')}
    >
      <FloatingIcon type="counsel" color="orange" />
      <span className="text-body-300 font-medium">상담 신청</span>
    </button>
  );
}
